import { useEffect, useMemo, useState } from 'react'
import { Code, RefreshCw, Search } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SigmaRule {
  id: string
  title: string
  description: string
  severity: string
  status: string
  author: string
  logsource: { product?: string; category?: string; service?: string }
  tags: string[]
  techniques: string[]
  references: string[]
}

const SEV_TONE: Record<string, string> = {
  critical:      'bg-rose/15 text-rose-light border-rose/40',
  high:          'bg-orange/15 text-orange border-orange/40',
  medium:        'bg-amber/15 text-amber-light border-amber/40',
  low:           'bg-blue/15 text-blue-light border-blue/40',
  informational: 'bg-surface-raised text-text-muted border-border',
}

export default function SigmaRulesPage() {
  const [rules, setRules] = useState<SigmaRule[]>([])
  const [coverage, setCoverage] = useState<Record<string, number>>({})
  const [bySeverity, setBySeverity] = useState<Record<string, number>>({})
  const [stale, setStale] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [filterSev, setFilterSev] = useState('all')
  const [filterTech, setFilterTech] = useState('')
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState<SigmaRule | null>(null)

  const fetchRules = async (sev = filterSev, tech = filterTech, query = q) => {
    setLoading(true)
    try {
      const r = await api.get('/api/cyber/sigma-rules', { params: {
        severity: sev !== 'all' ? sev : undefined,
        technique: tech || undefined,
        q: query || undefined,
        limit: 200,
      } })
      setRules(r.data?.rules || [])
      setCoverage(r.data?.coverage || {})
      setBySeverity(r.data?.by_severity || {})
      setStale(!!r.data?.stale)
    } finally { setLoading(false) }
  }

  useEffect(() => { fetchRules() }, [])

  const triggerRefresh = async () => {
    setRefreshing(true)
    try {
      await api.post('/api/cyber/internal/refresh-sigma', null, { params: { limit: 200 } })
      fetchRules()
    } catch {} finally { setRefreshing(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Code size={24} className="text-emerald" /> Sigma Rules
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            SigmaHQ community detection rules, indexed by ATT&CK technique. {rules.length} rules · coverage of {Object.keys(coverage).length} techniques.
          </p>
        </div>
        <button type="button" onClick={triggerRefresh} className="btn-secondary text-xs">
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh from SigmaHQ
        </button>
      </div>

      {stale && (
        <div className="card p-4 text-center text-text-muted text-sm">
          Sigma library hasn't been built yet. Click "Refresh from SigmaHQ" — capped to 200 rules per pull (GitHub rate limits).
        </div>
      )}

      {!stale && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
              <input value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchRules()}
                placeholder="search rules…"
                className="input pl-8 py-1.5 text-xs w-full" />
            </div>
            <select value={filterSev} onChange={e => { setFilterSev(e.target.value); fetchRules(e.target.value, filterTech, q) }}
              className="input py-1.5 text-xs">
              <option value="all">All severities ({rules.length})</option>
              {Object.entries(bySeverity).map(([s, n]) => <option key={s} value={s}>{s} ({n})</option>)}
            </select>
            <input value={filterTech} onChange={e => setFilterTech(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && fetchRules(filterSev, filterTech.toUpperCase(), q)}
              placeholder="ATT&CK technique e.g. T1078"
              className="input py-1.5 text-xs w-44" />
            <button type="button" onClick={() => fetchRules()} className="btn-ghost text-xs">Apply</button>
          </div>

          <div className="card p-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                  <th className="text-left py-2 px-2">Title</th>
                  <th className="text-left py-2 px-2">Severity</th>
                  <th className="text-left py-2 px-2">Logsource</th>
                  <th className="text-left py-2 px-2">Techniques</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={4} className="py-6 text-center text-text-muted text-[11px]">Loading…</td></tr>
                )}
                {!loading && rules.map(r => (
                  <tr key={r.id} onClick={() => setPicked(r)}
                    className="border-b border-border/50 hover:bg-surface-hover cursor-pointer">
                    <td className="py-1.5 px-2">
                      <div className="text-text-primary">{r.title}</div>
                      <div className="text-[9px] text-text-muted line-clamp-1">{r.description}</div>
                    </td>
                    <td className="py-1.5 px-2">
                      <span className={clsx('px-1.5 py-0.5 rounded border text-[9px] uppercase font-mono',
                        SEV_TONE[r.severity] || SEV_TONE.informational)}>
                        {r.severity}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 text-[10px] text-text-muted">
                      {[r.logsource.product, r.logsource.category, r.logsource.service].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="py-1.5 px-2 font-mono text-[10px] text-purple-300">
                      {r.techniques.slice(0, 4).join(', ')}{r.techniques.length > 4 && ` +${r.techniques.length - 4}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {picked && (
        <div className="fixed inset-0 z-[600] bg-black/70 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setPicked(null)}>
          <div className="card max-w-2xl w-full p-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-bold text-text-primary">{picked.title}</h2>
              <button type="button" onClick={() => setPicked(null)} className="text-text-muted hover:text-text-primary">×</button>
            </div>
            <p className="text-[11px] text-text-secondary mb-3">{picked.description}</p>
            <div className="space-y-1.5 text-[11px]">
              <div><b>Severity:</b> {picked.severity}</div>
              <div><b>Status:</b> {picked.status}</div>
              <div><b>Logsource:</b> {[picked.logsource.product, picked.logsource.category, picked.logsource.service].filter(Boolean).join(' / ')}</div>
              <div><b>ATT&CK techniques:</b> <span className="font-mono text-purple-300">{picked.techniques.join(', ')}</span></div>
              <div><b>Author:</b> {picked.author}</div>
              {picked.references.length > 0 && (
                <div>
                  <b>References:</b>
                  <ul className="list-disc ml-5 mt-1">
                    {picked.references.map(r => <li key={r}><a href={r} target="_blank" rel="noreferrer" className="text-blue-light hover:underline truncate inline-block max-w-full">{r}</a></li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
